package com.patronat.top.features.dashboard.presentation.mappers

import com.patronat.top.dto.DashboardStatsResponse
import com.patronat.top.dto.ModeratorDashboardStatsResponse
import com.patronat.top.dto.SuperAdminDashboardStatsResponse
import com.patronat.top.dto.UserRole
import com.patronat.top.dto.VenueOwnerDashboardStatsResponse
import com.patronat.top.features.dashboard.presentation.model.DashboardStatCardUi
import com.patronat.top.features.dashboard.presentation.model.DashboardStatMetricUi
import com.patronat.top.ui.theme.AppIcons
import kotlin.math.roundToLong

fun buildDashboardCards(
    role: UserRole,
    stats: DashboardStatsResponse?
): List<DashboardStatCardUi> {
    return when (role) {
        UserRole.SUPER_ADMIN -> stats?.superAdmin?.toCards().orEmpty()
        UserRole.MODERATOR -> stats?.moderator?.toCards().orEmpty()
        UserRole.BUSINESS_OWNER, UserRole.EMPLOYEE -> stats?.venueOwner?.toCards().orEmpty()
        UserRole.CLIENT -> emptyList()
    }
}

private fun SuperAdminDashboardStatsResponse.toCards(): List<DashboardStatCardUi> {
    return listOf(
        DashboardStatCardUi(
            id = "super_admin_users",
            title = "Користувачі",
            icon = { AppIcons.NavUsers },
            metrics = listOf(
                metric(label = "Загалом", value = totalUsersCount),
                metric(
                    label = "Нові за вчора",
                    value = newUsersYesterdayCount,
                    percentageBase = totalUsersCount
                )
            )
        ),
        DashboardStatCardUi(
            id = "super_admin_businesses",
            title = "Бізнеси",
            icon = { AppIcons.VenueOwner },
            metrics = listOf(
                metric(label = "Загалом", value = totalBusinessesCount),
                metric(
                    label = "Нові за вчора",
                    value = newBusinessesYesterdayCount,
                    percentageBase = totalBusinessesCount
                )
            )
        ),
        DashboardStatCardUi(
            id = "super_admin_venues",
            title = "Заклади",
            icon = { AppIcons.NavBusiness },
            metrics = listOf(
                metric(label = "Загалом", value = totalVenuesCount),
                metric(
                    label = "Заявки на приєднання",
                    value = venueJoinRequestsCount,
                    percentageBase = totalVenuesCount
                )
            )
        )
    )
}

private fun ModeratorDashboardStatsResponse.toCards(): List<DashboardStatCardUi> {
    val totalModerationRequests = venueModerationRequestsCount + reviewModerationRequestsCount

    return listOf(
        DashboardStatCardUi(
            id = "moderator_moderation_requests",
            title = "Модерація",
            icon = { AppIcons.AlertWarning },
            metrics = listOf(
                metric(
                    label = "Заклади",
                    value = venueModerationRequestsCount,
                    percentageBase = totalModerationRequests
                ),
                metric(
                    label = "Коментарі",
                    value = reviewModerationRequestsCount,
                    percentageBase = totalModerationRequests
                )
            )
        )
    )
}

private fun VenueOwnerDashboardStatsResponse.toCards(): List<DashboardStatCardUi> {
    val answeredReviewsCount = (totalReviewsCount - unansweredReviewsCount).coerceAtLeast(0)
    val todayAnsweredReviewsCount = (todayReviewsCount - todayUnansweredReviewsCount).coerceAtLeast(0)

    return listOf(
        DashboardStatCardUi(
            id = "owner_reviews_total",
            title = "Коментарі",
            icon = { AppIcons.NavReview },
            metrics = listOf(
                metric(
                    label = "Нові",
                    value = unansweredReviewsCount,
                    percentageBase = totalReviewsCount
                ),
                metric(
                    label = "З відповіддю",
                    value = answeredReviewsCount,
                    percentageBase = totalReviewsCount
                )
            )
        ),
        DashboardStatCardUi(
            id = "owner_reviews_today",
            title = "Коментарі за сьогодні",
            icon = { AppIcons.AlertCircle },
            metrics = listOf(
                metric(
                    label = "Нові",
                    value = todayUnansweredReviewsCount,
                    percentageBase = todayReviewsCount
                ),
                metric(
                    label = "З відповіддю",
                    value = todayAnsweredReviewsCount,
                    percentageBase = todayReviewsCount
                )
            )
        )
    )
}

private fun metric(
    label: String,
    value: Long,
    percentageBase: Long? = null
): DashboardStatMetricUi {
    return DashboardStatMetricUi(
        label = label,
        value = value.asCountText(),
        percentage = percentageBase?.asPercentageOf(value)
    )
}

private fun Long.asCountText(): String = toString()

private fun Long.asPercentageOf(part: Long): String {
    if (this <= 0L) return "+0.00%"

    val safePart = part.coerceAtLeast(0L)
    val basisPoints = ((safePart.toDouble() * 10_000.0) / toDouble())
        .roundToLong()
        .coerceAtLeast(0L)

    val whole = basisPoints / 100
    val fraction = (basisPoints % 100).toString().padStart(2, '0')

    return "+$whole.$fraction%"
}
